/* 180by2 product finder. The flow is defined in product-finder-data.json. */
(function () {
  'use strict';

  class QuizState {
    constructor(data) { this.data = data; this.reset(); }
    reset() { this.current = this.data.start; this.history = []; this.selected = null; }
    select(id) {
      if (!this.data.questions[this.current]?.options.some(option => option.id === id)) return false;
      this.selected = id;
      return true;
    }
    next() {
      const option = this.data.questions[this.current]?.options.find(option => option.id === this.selected);
      if (!option) return false;
      if (!this.data.questions[option.next] && !this.data.results[option.next]) return false;
      this.history.push({ node: this.current, answer: option.id, label: option.label });
      this.current = option.next;
      this.selected = null;
      return true;
    }
    back() {
      const previous = this.history.pop();
      if (!previous) return false;
      this.current = previous.node;
      this.selected = previous.answer;
      return true;
    }
    remaining(node = this.current) {
      const question = this.data.questions[node];
      return question ? 1 + Math.max(...question.options.map(option => this.remaining(option.next))) : 0;
    }
    get progress() {
      if (this.data.results[this.current]) return 100;
      return Math.round(this.history.length / (this.history.length + this.remaining()) * 100);
    }
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { QuizState };
  if (typeof window === 'undefined' || !window.customElements) return;

  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);

  class ProductFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.serial = 0;
      this.productCache = new Map();
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.addEventListener('change', event => {
        if (event.target.matches('[data-answer]') && this.state?.select(event.target.value)) {
          this.querySelector('[data-action="next"]').disabled = false;
        }
      });
      this.addEventListener('click', event => {
        const action = event.target.closest('[data-action]')?.dataset.action;
        if (!action) return;
        if (action === 'retry') { this.load(); return; }
        if (!this.state) return;
        if (action === 'next' && this.state.next()) this.render(true);
        if (action === 'back' && this.state.back()) this.render(true);
        if (action === 'reset') { this.state.reset(); this.render(true); }
      });
      this.load();
    }

    async load() {
      this.innerHTML = '<div class="q180__loading" role="status">Getting your product finder ready…</div>';
      try {
        const response = await fetch(this.dataset.source, { signal: AbortSignal.timeout(12000) });
        if (!response.ok) throw new Error('Quiz unavailable');
        this.data = await response.json();
        this.state = new QuizState(this.data);
        this.render(false);
      } catch (_) {
        this.innerHTML = `<div class="q180__loading"><h1>Let’s help you choose.</h1><p>The quiz couldn’t load. Try again or talk to our team.</p><button type="button" class="q180__primary" data-action="retry">Try again</button> <a href="${escape(this.link('pages/contact-us'))}">Contact 180by2</a></div>`;
      }
    }

    link(path) {
      const url = new URL(this.root + path, location.origin);
      const preview = new URLSearchParams(location.search).get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url.pathname + url.search + url.hash;
    }

    render(moveFocus) {
      const serial = ++this.serial;
      const state = this.state;
      const result = this.data.results[state.current];
      const question = this.data.questions[state.current];
      const theme = state.history[0]?.answer || 'all';
      this.dataset.category = theme;
      const progressText = result ? 'Your shortlist' : `Question ${state.history.length + 1}`;
      const trail = state.history.map(item => `<span>${escape(item.label)}</span>`).join('<span aria-hidden="true" class="q180__crumb-arrow">/</span>');
      this.innerHTML = `
        <div class="q180__shell">
          <div class="q180__toolbar">
            <a class="q180__exit" href="${escape(this.link(''))}"><span aria-hidden="true">←</span> Back to the shop</a>
            <span class="q180__wordmark">180by2 <span> / PRODUCT FINDER</span></span>
            ${state.history.length ? '<button type="button" class="q180__reset" data-action="reset">Start again</button>' : '<span class="q180__privacy">No sign-up needed</span>'}
          </div>
          <div class="q180__progress" role="progressbar" aria-label="Quiz progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${state.progress}" aria-valuetext="${progressText}"><span style="width:${state.progress}%"></span></div>
          <div class="q180__meta"><span>${progressText}</span><span>${result ? 'Chosen around your answers' : 'A few choices. A clearer direction.'}</span></div>
          ${trail ? `<div class="q180__trail" aria-label="Your answers">${trail}</div>` : ''}
          <div class="q180__heading">
            <p class="q180__eyebrow">${result ? 'MADE FOR YOUR NEXT MOVE' : 'FIND YOUR NEXT MOVE'}</p>
            <h1 tabindex="-1" data-heading>${escape(result ? result.title : question.title)}</h1>
            <p>${escape(result ? result.reason : question.hint)}</p>
          </div>
          ${result ? this.resultMarkup(result) : this.questionMarkup(question)}
          <div class="q180__footer">
            ${state.history.length ? '<button type="button" class="q180__back" data-action="back"><span aria-hidden="true">←</span> Back</button>' : '<span class="q180__small">Built around what you want to do.</span>'}
            ${result ? `<a class="q180__contact" href="${escape(this.link('pages/contact-us'))}">Talk it through with our team <span aria-hidden="true">↗</span></a>` : `<button type="button" class="q180__primary" data-action="next" ${state.selected ? '' : 'disabled'}>Continue <span aria-hidden="true">→</span></button>`}
          </div>
          <p class="q180__fine">${result ? 'Compare current prices, variants and availability on the product page.' : 'Your answers stay in this quiz. No email address required.'}</p>
        </div>`;
      if (result) this.hydrateProducts(result, serial);
      if (moveFocus) {
        const heading = this.querySelector('[data-heading]');
        heading.focus({ preventScroll: true });
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ block: 'start', behavior: reduced ? 'instant' : 'smooth' });
      }
    }

    questionMarkup(question) {
      const category = this.state.current === 'category';
      return `<fieldset class="q180__answers ${category ? 'q180__answers--categories' : ''}"><legend class="q180__sr">${escape(question.title)}</legend>${question.options.map((option, index) => `
        <label class="q180__option ${category ? 'q180__option--' + option.id : ''}">
          <input type="radio" name="q180-${escape(this.id)}" value="${escape(option.id)}" data-answer ${this.state.selected === option.id ? 'checked' : ''}>
          <span class="q180__option-surface">
            ${option.image ? `<span class="q180__option-image"><img src="${escape(option.image)}&width=600" alt="" width="600" height="450" loading="eager"><span class="q180__image-caption">${['ENTER NEW WORLDS', 'WEAR AI', 'CAPTURE EVERYTHING'][index]}</span></span>` : ''}
            <span class="q180__option-copy"><span class="q180__option-label">${escape(option.label)}</span>${option.detail ? `<span class="q180__option-detail">${escape(option.detail)}</span>` : ''}</span>
            <span class="q180__radio" aria-hidden="true"></span>
          </span>
        </label>`).join('')}</fieldset>`;
    }

    resultMarkup(result) {
      const cards = keys => keys.map((key, index) => {
        const product = this.data.products[key];
        return `<article class="q180__product-card" data-product="${escape(key)}"><div class="q180__product-placeholder" aria-hidden="true"><span>${index === 0 ? 'EXPLORE' : 'COMPARE'}</span></div><div class="q180__product-body"><h2>${escape(product.title)}</h2><p role="status">Checking current details…</p></div></article>`;
      }).join('');
      return `${result.note ? `<div class="q180__note"><span aria-hidden="true">i</span><p>${escape(result.note)}</p></div>` : ''}
        ${result.products.length ? `<div class="q180__results">${cards(result.products)}</div>` : `<div class="q180__help"><p>Share your budget and setup with us, and we’ll help you take the next step.</p><a class="q180__primary" href="${escape(this.link('pages/contact-us'))}">Help me choose <span aria-hidden="true">↗</span></a></div>`}
        ${result.accessories.length ? `<div class="q180__accessories"><h2>Complete your setup</h2><p>Accessories to explore separately. Confirm the right fit for your chosen camera.</p><div class="q180__results q180__results--accessories">${cards(result.accessories)}</div></div>` : ''}`;
    }

    async getProduct(handle) {
      if (!handle) return null;
      if (!this.productCache.has(handle)) {
        const promise = fetch(this.root + 'products/' + encodeURIComponent(handle) + '.js', { signal: AbortSignal.timeout(8000) })
          .then(response => { if (!response.ok) throw new Error('Unavailable'); return response.json(); })
          .catch(() => { this.productCache.delete(handle); return null; });
        this.productCache.set(handle, promise);
      }
      return this.productCache.get(handle);
    }

    money(cents) {
      const currency = window.Shopify?.currency?.active || this.dataset.currency || 'ZAR';
      return new Intl.NumberFormat(document.documentElement.lang || 'en-ZA', { style: 'currency', currency }).format(cents / 100);
    }

    budgetWarning(product) {
      const answer = this.state.history.find(item => item.node === 'budget')?.answer;
      const currency = window.Shopify?.currency?.active || this.dataset.currency || 'ZAR';
      if (!answer || currency !== 'ZAR') return '';
      const cents = Number(product.price_min);
      const outside = answer === 'low' ? cents >= 600000 : answer === 'mid' ? cents < 600000 || cents > 900000 : false;
      return outside ? '<p class="q180__stock">The current starting price is outside your selected range.</p>' : '';
    }

    async hydrateProducts(result, serial) {
      await Promise.all([...result.products, ...result.accessories].map(async key => {
        const configured = this.data.products[key];
        const live = await this.getProduct(configured.handle);
        if (serial !== this.serial || !this.isConnected) return;
        const card = this.querySelector(`[data-product="${key}"]`);
        if (!card) return;
        const variant = configured.variant_id && live?.variants.find(item => String(item.id) === String(configured.variant_id));
        if (!live || (configured.variant_id && !variant)) {
          const href = this.link('pages/contact-us');
          const status = configured.preorder ? 'Pre-order — not available for immediate purchase. Ask our team to confirm timing.' : 'Ask our team to confirm this option and availability.';
          card.innerHTML = `<div class="q180__product-placeholder" aria-hidden="true"><span>EXPLORE THE OPTIONS</span></div><div class="q180__product-body"><h2>${escape(configured.title)}</h2><p class="q180__stock">${status}</p><a class="q180__product-cta" href="${escape(href)}">Ask about this option <span aria-hidden="true">↗</span></a></div>`;
          return;
        }
        const href = this.link('products/' + encodeURIComponent(live.handle) + (variant ? '?variant=' + variant.id : ''));
        const available = variant ? variant.available : live.available;
        const price = variant ? variant.price : live.price_min;
        const imageSource = variant?.featured_image?.src || live.featured_image;
        const image = imageSource ? new URL(imageSource, location.origin) : null;
        if (image) image.searchParams.set('width', '700');
        const status = configured.preorder ? '<p class="q180__stock">Pre-order — not available for immediate purchase.</p>' : !available ? '<p class="q180__stock">Currently unavailable</p>' : '';
        const cta = configured.preorder ? 'View pre-order details' : available ? 'Explore product' : 'View availability';
        card.innerHTML = `${image ? `<a class="q180__product-photo" href="${escape(href)}" tabindex="-1" aria-hidden="true"><img src="${escape(image.href)}" alt="" width="700" height="600" loading="lazy"></a>` : '<div class="q180__product-placeholder" aria-hidden="true"></div>'}<div class="q180__product-body"><h2>${escape(configured.title)}</h2><p class="q180__price">${!variant && live.price_varies ? 'From ' : ''}${escape(this.money(price))}</p>${status}${this.budgetWarning({price_min: price})}<a class="q180__product-cta" href="${escape(href)}">${cta} <span aria-hidden="true">↗</span></a></div>`;
      }));
    }
  }

  if (!customElements.get('product-finder-quiz')) customElements.define('product-finder-quiz', ProductFinder);
})();
